import type { IconType } from "react-icons";
import { FaBed, FaIdCard, FaPlane, FaDumbbell } from "react-icons/fa";
import { FaUserCheck } from "react-icons/fa6";

export type TeamModuleKey =
    | "Form_Visa"
    | "Form_Transport"
    | "Form_Hotel"
    | "Form_Practice"
    | "Form_Accreditations";

export interface TeamModuleConfig {
    key: TeamModuleKey;
    titleEn: string;
    titleDa: string;
    shortEn: string;
    shortDa: string;
    longEn: string;
    longDa: string;
    deadlineEn: string;
    deadlineDa: string;
    deadlineDate?: string; // ISO-format, fx "2026-05-27"
    icon: IconType;
    alwaysOpen?: boolean;
    buildLink: (eventId: number, userId: number | string) => string;
}

export const teamModuleConfig: readonly TeamModuleConfig[] = [
    {
        key: "Form_Visa",
        titleEn: "Visa",
        titleDa: "Visa",
        shortEn: "Visa support and invitation letter request.",
        shortDa: "Visa-support og anmodning om invitationsbrev.",
        longEn:
            "Use this module to request visa support. You will be asked to provide personal details and upload any required documents.",
        longDa:
            "Brug dette modul til at anmode om visa-support. Her indtaster du oplysninger og uploader evt. dokumentation.",
        deadlineEn: "Deadline: 27 May 2026",
        deadlineDa: "Deadline: 27 maj 2026",
        deadlineDate: "2026-05-27",
        icon: FaIdCard,
        buildLink: (eventId, userId) => `/Team/Visa/${eventId}/${userId}`,
    },
    {
        key: "Form_Transport",
        titleEn: "Transport",
        titleDa: "Transport",
        shortEn: "Transport requests between venues and hubs.",
        shortDa: "Transport-forespørgsler mellem venues og hubs.",
        longEn:
            "Use this module to submit transport needs (airport/station, venue-to-venue etc.).",
        longDa:
            "Brug dette modul til at indsende transportbehov (lufthavn/station, venue-til-venue osv.).",
        deadlineEn: "Deadline: 3 may 2026",
        deadlineDa: "Deadline: 3 maj 2026",
        deadlineDate: "2026-05-03",
        icon: FaPlane,
        buildLink: (eventId, userId) => `/Team/Transport/Booking/${eventId}/${userId}`,
    },
    {
        key: "Form_Hotel",
        titleEn: "Accommodation",
        titleDa: "Indkvartering",
        shortEn: "Hotel booking information and requests.",
        shortDa: "Hotelbookinger og overnatning.",
        longEn:
            "Use this module to handle accommodation requests and confirm hotel bookings.",
        longDa:
            "Brug dette modul til indkvartering: forespørgsler og bekræftelser af hotelbookinger.",
        deadlineEn: "Deadline: 8 March 2026",
        deadlineDa: "Deadline: 8 marts 2026",
        deadlineDate: "2026-03-08",
        icon: FaBed,
        buildLink: (eventId, userId) => `/Team/Hotel/${eventId}/${userId}`,
    },
    {
        key: "Form_Practice",
        titleEn: "Practice",
        titleDa: "Træning",
        shortEn: "Practice schedule and booking.",
        shortDa: "Træningstider og booking.",
        longEn:
            "Use this module to manage practice time requests and allocations.",
        longDa:
            "Brug dette modul til at håndtere ønsker til træningstider og fordeling.",
        deadlineEn: "Deadline: 4 May 2026",
        deadlineDa: "Deadline: 4 maj 2026",
        deadlineDate: "2026-05-04",
        icon: FaDumbbell,
        buildLink: (eventId, userId) => `/Team/Practice/${eventId}/${userId}`,
    },
    {
        key: "Form_Accreditations",
        titleEn: "Accreditations",
        titleDa: "Akkrediteringer",
        shortEn: "Register team members for accreditation.",
        shortDa: "Registrér holdmedlemmer til akkreditering.",
        longEn:
            "Use this module to create and manage accreditations for players, coaches and staff. This module is always open until the final deadline.",
        longDa:
            "Brug dette modul til at oprette og håndtere akkrediteringer for spillere, trænere og staff. Dette modul er altid åbent frem til den endelige deadline.",
        deadlineEn: "Deadline: 03 May 2026",
        deadlineDa: "Deadline: 03 maj 2026",
        deadlineDate: "2026-05-03",
        icon: FaUserCheck,
        alwaysOpen: true,
        buildLink: (eventId, userId) => `/Team/Accreditations/${eventId}/${userId}`,
    },
] as const;